import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PresetCard from './PresetCard';
import { SEARCH_EXPERIENCES as MODE_OPTIONS } from '../integration-wizard/experienceOptions';
import SaveIcon from '@material-ui/icons/Save';
import RefreshIcon from '@material-ui/icons/Refresh';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import SearchFeaturePageShell from '../../components/shell/SearchFeaturePageShell';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import { usePreviewOpener } from '../shell/preview/usePreviewOpener';
import { ADMIN_RUNTIME_COMMANDS } from '../../app/runtime/adminRuntime';
import LiveKillSwitchControl from '../../components/admin/LiveKillSwitchControl';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../../actions/Notifications';
import { getStepsForExperience } from '../integration-wizard/embedded/config/embeddedWizardConfig';
import { useBundleRebuild } from '../home/components/Diagnostics/useBundleRebuild';
import PublishProgressDialog from '../home/components/PublishProgressDialog';
import {
  useOperationDialogVisibility,
} from '../home/components/PublishProgressDialog/useOperationDialogVisibility';
import { notifyOperatorStateRefresh } from '../home/livePublishEvents';
import { loadConfigSuccess } from '../search-experience-v2/api/searchExperienceSlice';
import { normalizeRuntimeSaveOutcome } from '../../app/runtime/saveOutcome';

const MODE_WIZARD_TYPES = {
  unified: 'unified',
  classic: 'classic',
  fullpage: 'fullpage',
};

const STEP_REQUIREMENTS = {
  'searchbox-config': 'Search box setup',
  'classic-config': 'Search dropdown setup',
  'dropdown-integration': 'Search dropdown placement',
  'resultspage-config': 'Results page setup',
  'resultspage-integration': 'Results page placement',
};

const SEARCH_TRIGGER_FIELDS = {
  searchBox: {
    desktop: 'selectors',
    mobile: 'mobileSelectors',
  },
  dropdown: {
    desktop: 'inputSelectors',
    mobile: 'mobileInputSelectors',
  },
};

const normalizeMode = (mode) => (MODE_WIZARD_TYPES[mode] ? mode : 'unified');

export const getSearchModeRequirements = (mode) => {
  const wizardType = MODE_WIZARD_TYPES[normalizeMode(mode)] || MODE_WIZARD_TYPES.unified;
  const seen = new Set();
  return getStepsForExperience(wizardType)
    .map((step) => STEP_REQUIREMENTS[step.id])
    .filter(Boolean)
    .filter((label) => {
      if (seen.has(label)) return false;
      seen.add(label);
      return true;
    });
};

const copySearchTriggerSelectors = ({
  sourceIntegration,
  sourceFields,
  targetIntegration,
  targetFields,
}) => {
  if (!sourceIntegration) return targetIntegration;

  const nextIntegration = { ...(targetIntegration || {}) };
  const desktopSelectors = sourceIntegration[sourceFields.desktop] || [];
  const mobileSelectors = sourceIntegration[sourceFields.mobile] || [];

  if (desktopSelectors.length > 0 && !nextIntegration[targetFields.desktop]?.length) {
    nextIntegration[targetFields.desktop] = desktopSelectors;
  }
  if (mobileSelectors.length > 0 && !nextIntegration[targetFields.mobile]?.length) {
    nextIntegration[targetFields.mobile] = mobileSelectors;
  }

  return nextIntegration;
};

const useStyles = makeStyles((theme) => ({
  cards: {
    marginTop: theme.spacing(1),
  },
  modeNotice: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.info ? theme.palette.info.light : '#90caf9'}`,
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(33, 150, 243, 0.14)'
      : 'rgba(33, 150, 243, 0.08)',
    color: theme.palette.text.primary,
  },
  modeNoticeIcon: {
    color: theme.palette.info ? theme.palette.info.main : '#1976d2',
    fontSize: 20,
    marginTop: 1,
  },
  modeNoticeText: {
    margin: 0,
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
  section: {
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #ddeae5',
    borderRadius: 8,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    transition: theme.transitions.create(['border-color', 'background-color']),
  },
  sectionDanger: {
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(244, 67, 54, 0.12)'
      : 'rgba(211, 47, 47, 0.04)',
  },
  sectionPending: {
    borderColor: theme.palette.warning ? theme.palette.warning.main : '#f9a825',
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 193, 7, 0.12)'
      : 'rgba(255, 193, 7, 0.08)',
  },
  sectionHeader: {
    marginBottom: theme.spacing(2),
  },
  killSwitchHeader: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  killSwitchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  killSwitchText: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  killSwitchControl: {
    flex: '0 0 auto',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1.05rem',
    marginBottom: theme.spacing(0.5),
  },
  sectionDescription: {
    color: theme.palette.text.secondary,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 24,
    padding: theme.spacing(0.25, 1),
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.2,
    border: '1px solid transparent',
  },
  statusBadgeLive: {
    color: theme.palette.success ? theme.palette.success.dark : '#1b5e20',
    borderColor: theme.palette.success ? theme.palette.success.main : '#2e7d32',
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(76, 175, 80, 0.12)'
      : 'rgba(46, 125, 50, 0.08)',
  },
  statusBadgeOff: {
    color: theme.palette.error.dark,
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(244, 67, 54, 0.14)'
      : 'rgba(211, 47, 47, 0.08)',
  },
  statusBadgePending: {
    color: theme.palette.warning ? theme.palette.warning.dark : '#7a4f01',
    borderColor: theme.palette.warning ? theme.palette.warning.main : '#f9a825',
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 193, 7, 0.14)'
      : 'rgba(255, 193, 7, 0.12)',
  },
  statusBadgeMuted: {
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.action.hover,
  },
}));

const cloneConfig = (value) => JSON.parse(JSON.stringify(value));

export const buildSearchModeConfig = (currentConfig, selectedMode, targetDefaults) => {
  const normalizedMode = normalizeMode(selectedMode);
  if (!targetDefaults?.search) {
    throw new Error('Gateway defaults are required before changing search mode.');
  }

  const draft = cloneConfig(targetDefaults);
  const currentSearch = currentConfig?.search || {};
  const currentViews = currentSearch.views || {};
  const nextViews = draft.search.views || {};

  Object.keys(nextViews).forEach((viewKey) => {
    const currentView = currentViews[viewKey];
    if (!currentView) return;

    nextViews[viewKey] = {
      ...nextViews[viewKey],
      integration: currentView.integration || nextViews[viewKey].integration,
      mobileIntegration: currentView.mobileIntegration || nextViews[viewKey].mobileIntegration,
    };
  });

  if (nextViews.searchBox && currentViews.dropdown && !currentViews.searchBox) {
    nextViews.searchBox = {
      ...nextViews.searchBox,
      integration: copySearchTriggerSelectors({
        sourceIntegration: currentViews.dropdown.integration,
        sourceFields: SEARCH_TRIGGER_FIELDS.dropdown,
        targetIntegration: nextViews.searchBox.integration,
        targetFields: SEARCH_TRIGGER_FIELDS.searchBox,
      }),
    };
  }

  if (nextViews.dropdown && currentViews.searchBox && !currentViews.dropdown) {
    nextViews.dropdown = {
      ...nextViews.dropdown,
      integration: copySearchTriggerSelectors({
        sourceIntegration: currentViews.searchBox.integration,
        sourceFields: SEARCH_TRIGGER_FIELDS.searchBox,
        targetIntegration: nextViews.dropdown.integration,
        targetFields: SEARCH_TRIGGER_FIELDS.dropdown,
      }),
    };
  }

  return {
    ...draft,
    ...(currentConfig?._editorState ? { _editorState: currentConfig._editorState } : {}),
    search: {
      ...draft.search,
      searchExperience: normalizedMode,
      theme: currentSearch.theme || draft.search.theme,
      behavior: currentSearch.behavior || draft.search.behavior,
      views: nextViews,
    },
  };
};

const assertSaveSucceeded = (result, fallbackMessage) => {
  const outcome = normalizeRuntimeSaveOutcome(result);
  if (outcome.success && outcome.errors.length === 0) return outcome;
  const first = outcome.errors[0];
  const message = first?.message
    || first?.error
    || result?.error
    || fallbackMessage;
  throw new Error(message);
};

const SearchModePage = ({ client }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const localizeMessage = useLocalizedMessage();
  const localizeMessageRef = useRef(localizeMessage);
  localizeMessageRef.current = localizeMessage;
  const currentShop = useSelector((state) => state.app.currentShop);
  const {
    gatewayClient: shellGatewayClient,
    searchSettingsClient,
    productProfile,
    runtime,
  } = useShellAppContext();
  const effectiveClient = client || searchSettingsClient || shellGatewayClient;

  const [config, setConfig] = useState(null);
  const [selectedMode, setSelectedMode] = useState('unified');
  const [savedMode, setSavedMode] = useState('unified');
  const [savedLive, setSavedLive] = useState(null);
  const [pendingLive, setPendingLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const {
    canOpenPreview,
    openStorefrontPreview,
  } = usePreviewOpener();
  const previewBuild = useBundleRebuild();
  const previewDialog = useOperationDialogVisibility(previewBuild.status);
  const previewBuildSupported = runtime?.supports?.(
    ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_PREVIEW,
  ) === true;

  const loadConfig = useCallback(async () => {
    if (!effectiveClient || !currentShop) return;

    setLoading(true);
    setError(null);
    try {
      const loaded = await effectiveClient.loadSearchExperienceConfig(currentShop, 'preview');
      const mode = normalizeMode(loaded?.search?.searchExperience);
      setConfig(loaded);
      setSelectedMode(mode);
      setSavedMode(mode);
    } catch (err) {
      setError(err.message || localizeMessageRef.current('search.mode.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentShop, effectiveClient]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const hasChanges = selectedMode !== savedMode;
  const liveHasChanges = pendingLive !== null
    && savedLive !== null
    && pendingLive.enabled !== savedLive.enabled;

  const handleLiveStateChange = useCallback((payload) => {
    if (payload.source === 'load') {
      setSavedLive((current) => current || {
        enabled: payload.enabled,
        reason: payload.reason,
      });
      return;
    }
    if (payload.source === 'pending') {
      setPendingLive({ enabled: payload.enabled, reason: payload.reason });
    }
  }, []);

  const handleDiscard = useCallback(() => {
    setSelectedMode(savedMode);
    setPendingLive(null);
  }, [savedMode]);

  const sendRuntimeCommand = useCallback(async (command, payload = {}) => {
    if (!runtime?.supports?.(command)) {
      throw new Error(`Admin runtime does not support ${command}.`);
    }
    return runtime.sendMessageWithResponse(command, { shopId: currentShop, ...payload });
  }, [currentShop, runtime]);

  const savePreset = useCallback(async (mode) => {
    if (typeof effectiveClient.loadSearchExperienceDefaults !== 'function') {
      throw new Error('Gateway search experience defaults endpoint is not available. Refresh the embedded admin and try again.');
    }
    const targetDefaults = await effectiveClient.loadSearchExperienceDefaults(
      currentShop,
      mode,
      'preview',
    );
    const nextConfig = buildSearchModeConfig(config, mode, targetDefaults);
    const previewResult = await effectiveClient.saveSearchExperienceConfig(
      currentShop,
      nextConfig,
      'preview',
    );
    assertSaveSucceeded(previewResult, localizeMessage('search.mode.saveFailed'));
    await sendRuntimeCommand(ADMIN_RUNTIME_COMMANDS.ONBOARDING_MODE_SELECTED, {
      selectedMode: mode,
    });
    await sendRuntimeCommand(ADMIN_RUNTIME_COMMANDS.SETTINGS_PREVIEW_SAVED);

    const savedConfig = previewResult?.config || (previewResult?.search ? previewResult : nextConfig);
    setConfig(savedConfig);
    setSavedMode(mode);
    dispatch(loadConfigSuccess({ config: savedConfig, mode: 'preview' }));
    notifyOperatorStateRefresh();
  }, [config, currentShop, dispatch, effectiveClient, localizeMessage, sendRuntimeCommand]);

  const handlePresetPreview = async (mode) => {
    if (saving || !previewBuildSupported || !canOpenPreview) return;
    setSaving(true);
    try {
      if (mode === savedMode) {
        await openStorefrontPreview();
        return;
      }
      await savePreset(mode);
      setSelectedMode(mode);
      previewBuild.reset();
      previewDialog.begin();
      await previewBuild.startPreviewBuild(currentShop);
    } catch (err) {
      dispatch(enqueueErrorSnackbar(err.message || localizeMessage('search.mode.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const liveStatus = useMemo(() => {
    if (!savedLive) {
      return {
        label: localizeMessage('search.settings.live.badge.checking'),
        badgeClassName: `${classes.statusBadge} ${classes.statusBadgeMuted}`,
        sectionClassName: classes.section,
        sectionState: 'checking',
      };
    }

    if (liveHasChanges && pendingLive) {
      if (pendingLive.enabled) {
        return {
          label: localizeMessage('search.settings.live.badge.pending_on'),
          badgeClassName: `${classes.statusBadge} ${classes.statusBadgePending}`,
          sectionClassName: `${classes.section} ${classes.sectionPending}`,
          sectionState: 'pending-on',
        };
      }

      return {
        label: localizeMessage('search.settings.live.badge.pending_off'),
        badgeClassName: `${classes.statusBadge} ${classes.statusBadgeOff}`,
        sectionClassName: `${classes.section} ${classes.sectionDanger}`,
        sectionState: 'pending-off',
      };
    }

    if (savedLive.enabled) {
      return {
        label: localizeMessage('search.settings.live.badge.live'),
        badgeClassName: `${classes.statusBadge} ${classes.statusBadgeLive}`,
        sectionClassName: classes.section,
        sectionState: 'live',
      };
    }

    return {
      label: localizeMessage('search.settings.live.badge.off'),
      badgeClassName: `${classes.statusBadge} ${classes.statusBadgeOff}`,
      sectionClassName: `${classes.section} ${classes.sectionDanger}`,
      sectionState: 'off',
    };
  }, [
    classes.section,
    classes.sectionDanger,
    classes.sectionPending,
    classes.statusBadge,
    classes.statusBadgeLive,
    classes.statusBadgeMuted,
    classes.statusBadgeOff,
    classes.statusBadgePending,
    liveHasChanges,
    localizeMessage,
    pendingLive,
    savedLive,
  ]);

  const actions = useMemo(() => (
    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<RefreshIcon />}
        disabled={saving || loading || (!hasChanges && !liveHasChanges)}
        onClick={handleDiscard}
        data-testid="search-mode-discard"
      >
        {localizeMessage('common.discard')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
        disabled={
          saving
          || loading
          || !config
          || (!hasChanges && !liveHasChanges)
        }
        onClick={async () => {
          setSaving(true);
          try {
            if (hasChanges) {
              await savePreset(selectedMode);
              if (previewBuildSupported) {
                if (typeof previewBuild.reset === 'function') {
                  previewBuild.reset();
                }
                previewDialog.begin();
                await previewBuild.startPreviewBuild(currentShop);
              } else {
                dispatch(enqueueSuccessSnackbar(localizeMessage('search.page.searchMode.saved')));
              }
            }

            if (productProfile?.id !== 'cloud' && liveHasChanges && pendingLive) {
              await sendRuntimeCommand(ADMIN_RUNTIME_COMMANDS.LIVE_SET_ENABLED, {
                enabled: pendingLive.enabled,
              });
              setSavedLive(pendingLive);
              setPendingLive(null);
            }

            if (!hasChanges) {
              dispatch(enqueueSuccessSnackbar(localizeMessage('search.page.searchMode.saved')));
            }
          } catch (err) {
            dispatch(enqueueErrorSnackbar(err.message || localizeMessage('search.mode.saveFailed')));
          } finally {
            setSaving(false);
          }
        }}
        data-testid="search-mode-save-preview"
        data-preview-build-supported={previewBuildSupported ? 'true' : 'false'}
      >
        {saving
          ? localizeMessage('search.page.searchMode.saving')
          : localizeMessage('search.page.searchMode.save')}
      </Button>
    </Box>
  ), [
    config,
    currentShop,
    dispatch,
    effectiveClient,
    hasChanges,
    handleDiscard,
    liveHasChanges,
    loading,
    localizeMessage,
    pendingLive,
    productProfile,
    previewBuild,
    previewBuildSupported,
    previewDialog,
    saving,
    selectedMode,
    sendRuntimeCommand,
    savePreset,
  ]);

  if (!effectiveClient) {
    return (
      <SearchFeaturePageShell
        pageId="searchMode"
        error={localizeMessage('ui.searchSettings.searchSettingsPage.sessionUnavailable')}
        onRetry={() => { window.location.href = '/embedded/app'; }}
      />
    );
  }

  return (
    <SearchFeaturePageShell
      pageId="searchMode"
      description={localizeMessage('search.page.searchMode.description')}
      actions={actions}
      isLoading={loading}
      error={error}
      onRetry={loadConfig}
    >
      {productProfile?.id !== 'cloud' && (
      <Paper
        className={liveStatus.sectionClassName}
        elevation={0}
        data-testid="section-kill-switch"
        data-live-state={liveStatus.sectionState}
      >
        <Box className={classes.killSwitchRow}>
          <Box className={classes.killSwitchText}>
            <Box className={classes.killSwitchHeader}>
              <Typography variant="h6" className={classes.sectionTitle}>
                {localizeMessage('search.settings.integration.section.killSwitch.title')}
              </Typography>
              <span className={liveStatus.badgeClassName} data-testid="live-status-badge">
                {liveStatus.label}
              </span>
            </Box>
            <Typography variant="body2" className={classes.sectionDescription}>
              {localizeMessage('search.settings.integration.section.killSwitch.description')}
            </Typography>
          </Box>
          <Box className={classes.killSwitchControl}>
            <LiveKillSwitchControl
              bare
              deferWrite
              hideRefresh
              onLiveStateChange={handleLiveStateChange}
            />
          </Box>
        </Box>
      </Paper>
      )}

      <Paper className={classes.section} elevation={0} data-testid="section-search-mode">
        <Box className={classes.sectionHeader}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {localizeMessage('search.settings.integration.section.mode.title')}
          </Typography>
          <Typography variant="body2" className={classes.sectionDescription}>
            {localizeMessage('search.settings.integration.section.mode.description')}
          </Typography>
        </Box>
        <Grid container spacing={3} className={classes.cards}>
          {MODE_OPTIONS.map((option) => (
            <Grid item xs={12} md={4} key={option.type}>
              <PresetCard
                option={option}
                selected={selectedMode === option.type}
                disabled={saving}
                previewDisabled={!previewBuildSupported || !canOpenPreview}
                onSelect={() => setSelectedMode(option.type)}
                onPreview={() => handlePresetPreview(option.type)}
                t={localizeMessage}
              />
            </Grid>
          ))}
        </Grid>
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
          {localizeMessage(!previewBuildSupported || !canOpenPreview
            ? 'search.presets.previewUnavailable'
            : 'search.presets.previewHint')}
        </Typography>
        {hasChanges && (
          <Box className={classes.modeNotice} data-testid="search-mode-change-notice">
            <InfoOutlinedIcon className={classes.modeNoticeIcon} />
            <Typography variant="body2" className={classes.modeNoticeText}>
              {localizeMessage('search.page.searchMode.modeNote')}
            </Typography>
          </Box>
        )}
      </Paper>
      {previewBuildSupported && <PublishProgressDialog
        open={previewDialog.open}
        operation={previewBuild.operation || 'preview'}
        status={previewBuild.status}
        error={previewBuild.error}
        onRetry={() => {
          if (typeof previewBuild.reset === 'function') {
            previewBuild.reset();
          }
          previewDialog.begin();
          previewBuild.startPreviewBuild(currentShop);
        }}
        onClose={previewDialog.close}
        onOpenPreview={openStorefrontPreview}
        canOpenPreview={canOpenPreview}
        localizeMessage={localizeMessage}
      />}
    </SearchFeaturePageShell>
  );
};

SearchModePage.propTypes = {
  client: PropTypes.shape({
    loadSearchExperienceConfig: PropTypes.func.isRequired,
    saveSearchExperienceConfig: PropTypes.func.isRequired,
  }),
};

SearchModePage.defaultProps = {
  client: null,
};

export default SearchModePage;
